/**
 * Shared string formatting utilities.
 */

import { FRIENDLY_SAGA_NAMES, isSagaType } from '@/constants/sagaTypes'
import type { components } from '@/types/api/v1'

type VolumeUnits = components['schemas']['VolumeUnits']

/**
 * Formats a volume value with the appropriate unit suffix.
 *
 * Examples:
 *   (1500, "gallons") -> "1,500 gal"
 *   (1500, "liters") -> "1,500 L"
 *   (null, "gallons") -> "-"
 *   (1500, undefined) -> "1,500 gal" (defaults to gallons)
 */
export function formatVolume(
  value: number | null | undefined,
  units: VolumeUnits | null | undefined,
): string {
  if (value == null) return '-'
  const suffix = units === 'liters' ? 'L' : 'gal'
  return `${value.toLocaleString()} ${suffix}`
}

/**
 * Formats saga event information for display in the UI. Labels come from
 * FRIENDLY_SAGA_NAMES (which mirrors the backend's own names), so the grids
 * and the log details panel speak the same vocabulary; a saga type missing
 * from that map falls back to title-casing its raw value.
 *
 * Examples:
 *   ("create_order", "abc12345-...") -> { typeLabel: "Order Creation", shortId: "abc12345", fullId: "abc12345-..." }
 *   ("progress_drop", null) -> { typeLabel: "Save Drop", shortId: "", fullId: null }
 *   ("some_new_saga", "abc12345-...") -> { typeLabel: "Some New Saga", shortId: "abc12345", fullId: "abc12345-..." }
 *   (null, null) -> null
 */
export function formatSagaEvent(
  sagaType: string | null | undefined,
  sagaId: string | null | undefined,
): { typeLabel: string; shortId: string; fullId: string | null } | null {
  if (!sagaType && !sagaId) return null
  const typeLabel = sagaType
    ? isSagaType(sagaType)
      ? FRIENDLY_SAGA_NAMES[sagaType]
      : sagaType
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (c: string) => c.toUpperCase())
    : 'Unknown'
  const shortId = sagaId ? sagaId.slice(0, 8) : ''
  return { typeLabel, shortId, fullId: sagaId || null }
}

/**
 * Gets initials from a name for avatar display.
 * Handles names with spaces, underscores, dots, and dashes.
 *
 * Examples:
 *   "John Doe" -> "JD"
 *   "john.doe" -> "JD"
 *   "jane_smith" -> "JS"
 *   "Bob" -> "BO"
 *   null -> "?"
 */
export function getInitials(name: string | null | undefined): string {
  if (!name) return '?'
  const parts = name.split(/[\s._-]+/).filter((p): p is string => Boolean(p))
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase()
  }
  return name.slice(0, 2).toUpperCase()
}

/**
 * Formats a 0..1 ratio as a display percentage.
 *
 * Examples:
 *   (0.9521) -> "95.2%"
 *   (1) -> "100.0%"
 *   (null) -> "-"
 */
export function formatPercent(
  value: number | null | undefined,
  digits: number = 1,
): string {
  if (value == null) return '-'
  return `${(value * 100).toFixed(digits)}%`
}

/**
 * Formats a whole-number count with thousands separators.
 *
 * Examples:
 *   (1128) -> "1,128"
 *   (null) -> "-"
 */
export function formatCount(value: number | null | undefined): string {
  if (value == null) return '-'
  return value.toLocaleString()
}
